import React, { useState, useEffect } from 'react';
import { 
  useFetchUsersQuery, 
  useCreateUserMutation, 
  useUpdateUserMutation, 
  useDeleteUserMutation 
} from '../../services/usersApiSlice';
import { X, Plus, Minus, Search, Pencil } from 'lucide-react';
import Button from '../Button/Button';

interface ThemeColors {
  primary?: string;
  textPrimary?: string;
  textSecondary?: string;
  backgroundPrimary?: string;
  backgroundSecondary?: string;
  backgroundTertiary?: string;
  borderColor?: string;
  [key: string]: unknown;
}

interface UserCrudModalProps {
  isOpen: boolean;
  onClose: () => void;
  theme: ThemeColors;
}

interface UserRecord {
  id: string;
  userName?: string;
  userEmail?: string;
  groupName?: string;
}

const UserCrudModal = ({ isOpen, onClose, theme }: UserCrudModalProps): React.ReactElement | null => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');
  const [selectedUser, setSelectedUser] = useState<UserRecord | null>(null);
  const [deleteMode, setDeleteMode] = useState(false);
  const [usersToDelete, setUsersToDelete] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState({
    userName: '',
    userEmail: '',
    groupName: ''
  });

  const { data: users = [], isLoading } = useFetchUsersQuery({});
  const [createUser] = useCreateUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const [deleteUser] = useDeleteUserMutation();

  const filteredUsers = (users as UserRecord[]).filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (user.userName || '').toLowerCase().includes(searchLower) ||
      (user.userEmail || '').toLowerCase().includes(searchLower) ||
      (user.groupName || '').toLowerCase().includes(searchLower)
    );
  });

  useEffect(() => {
    if (!isOpen) {
      setShowForm(false);
      setFormMode('add');
      setSelectedUser(null);
      setFormData({ userName: '', userEmail: '', groupName: '' });
      setSearchTerm('');
      setDeleteMode(false);
      setUsersToDelete(new Set());
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedUser && formMode === 'edit') {
      setFormData({
        userName: selectedUser.userName || '',
        userEmail: selectedUser.userEmail || '',
        groupName: selectedUser.groupName || ''
      });
    }
  }, [selectedUser, formMode]);

  const handleAddClick = (): void => {
    setFormMode('add');
    setSelectedUser(null);
    setFormData({ userName: '', userEmail: '', groupName: '' });
    setShowForm(true);
  };

  const handleEditClick = (user: UserRecord): void => {
    setFormMode('edit');
    setSelectedUser(user);
    setShowForm(true);
  };

  const handleDeleteClick = async (user: UserRecord): Promise<void> => {
    if (window.confirm(`Are you sure you want to delete ${user.userName || user.userEmail}?`)) {
      try {
        await deleteUser({ userId: user.id }).unwrap();
      } catch (error) {
        alert(`Error deleting user: ${(error as Error).message || 'Unknown error'}`);
      }
    }
  };

  const handleDeleteModeToggle = (): void => {
    setDeleteMode(!deleteMode);
    setUsersToDelete(new Set());
  };

  const handleToggleUserForDelete = (userId: string): void => {
    const newSet = new Set(usersToDelete);
    if (newSet.has(userId)) {
      newSet.delete(userId);
    } else {
      newSet.add(userId);
    }
    setUsersToDelete(newSet);
  };

  const handleBulkDelete = async (): Promise<void> => {
    if (usersToDelete.size === 0) {
      alert('Please select at least one user to delete');
      return;
    }

    if (window.confirm(`Are you sure you want to delete ${usersToDelete.size} user(s)?`)) {
      try {
        const deletePromises = Array.from(usersToDelete).map(userId =>
          deleteUser({ userId }).unwrap()
        );
        await Promise.all(deletePromises);
        setDeleteMode(false);
        setUsersToDelete(new Set());
      } catch (error) {
        alert(`Error deleting users: ${(error as Error).message || 'Unknown error'}`);
      }
    }
  };

  const handleFormSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    if (!formData.userName.trim() || !formData.userEmail.trim() || !formData.groupName.trim()) {
      alert('Please fill in all fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.userEmail)) {
      alert('Please enter a valid email address');
      return;
    }

    try {
      if (formMode === 'add') {
        const result = await createUser({
          userName: formData.userName.trim(),
          userEmail: formData.userEmail.trim(),
          groupName: formData.groupName.trim()
        }).unwrap();
        
        if (result && (result as { id?: string }).id) {
          setSelectedUser(result as UserRecord);
          setFormMode('edit');
        } else {
          setShowForm(false);
          setFormData({ userName: '', userEmail: '', groupName: '' });
          setSelectedUser(null);
        }
      } else if (formMode === 'edit' && selectedUser) {
        await updateUser({
          userId: selectedUser.id,
          userName: formData.userName.trim(),
          userEmail: formData.userEmail.trim(),
          groupName: formData.groupName.trim()
        }).unwrap();
        
        setShowForm(false);
        setFormData({ userName: '', userEmail: '', groupName: '' });
        setSelectedUser(null);
      }
    } catch (error) {
      alert(`Error ${formMode === 'add' ? 'creating' : 'updating'} user: ${(error as Error).message || 'Unknown error'}`);
    }
  };

  const handleCancel = (): void => {
    setShowForm(false);
    setFormData({ userName: '', userEmail: '', groupName: '' });
    setSelectedUser(null);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget && !showForm) {
          onClose();
        }
      }}
    >
      <div
        className="rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col"
        style={{ 
          backgroundColor: theme?.backgroundPrimary || '#fff',
          color: theme?.textPrimary || '#333'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div 
          className="relative flex items-center justify-center p-6 border-b"
          style={{ borderColor: theme?.borderColor || '#e0e0e0' }}
        >
          <h1 
            className="text-2xl font-semibold text-center"
            style={{ color: theme?.textPrimary || '#333'}}
          >
            SDS Users Group
          </h1>
          <Button
            onClick={onClose}
            className="absolute right-6 top-1/2 -translate-y-1/2 p-2 rounded hover:bg-opacity-20"
            style={{ 
              color: theme?.textPrimary || '#333',
              backgroundColor: 'transparent'
            }}
            aria-label="Close modal"
          >
            <X size={24} />
          </Button>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col p-6">
          {!showForm && (
            <div className="mb-4 space-y-2">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search 
                    size={20} 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2"
                    style={{ color: theme?.textSecondary || '#666' }}
                  />
                  <input
                    type="text"
                    placeholder="Search users by name, email, or group..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg outline-none"
                    style={{
                      backgroundColor: theme?.backgroundTertiary || '#f5f5f5',
                      color: theme?.textPrimary || '#333',
                      borderColor: theme?.borderColor || '#e0e0e0'
                    }}
                  />
                </div>
                <Button
                  onClick={handleAddClick}
                  style={{ backgroundColor: theme?.primary || '#7c4dff' }}
                >
                  <Plus size={20} />
                  Add
                </Button>
                <Button
                  onClick={handleDeleteModeToggle}
                  style={{ 
                    backgroundColor: '#ef4444',
                    opacity: deleteMode ? 1 : 0.8
                  }}
                >
                  <Minus size={20} />
                  Delete
                </Button>
              </div>
              {deleteMode && (
                <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: theme?.backgroundSecondary || '#f5f7fb' }}>
                  <span style={{ color: theme?.textPrimary || '#333' }}>
                    {usersToDelete.size} user(s) selected
                  </span>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleDeleteModeToggle}
                      variant="outline"
                      style={{
                        backgroundColor: theme?.backgroundTertiary || '#f5f5f5',
                        color: theme?.textPrimary || '#333',
                        borderColor: theme?.borderColor || '#e0e0e0'
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleBulkDelete}
                      style={{ backgroundColor: '#ef4444' }}
                      disabled={usersToDelete.size === 0}
                    >
                      Delete Selected
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {showForm && (
            <div className="mb-4">
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div>
                  <label 
                    className="block mb-2 font-medium"
                    style={{ color: theme?.textPrimary || '#333' }}
                  >
                    User Name *
                  </label>
                  <input
                    type="text"
                    value={formData.userName}
                    onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg outline-none"
                    style={{
                      backgroundColor: theme?.backgroundTertiary || '#f5f5f5',
                      color: theme?.textPrimary || '#333',
                      borderColor: theme?.borderColor || '#e0e0e0'
                    }}
                    required
                  />
                </div>
                <div>
                  <label 
                    className="block mb-2 font-medium"
                    style={{ color: theme?.textPrimary || '#333' }}
                  >
                    User Email *
                  </label>
                  <input
                    type="email"
                    value={formData.userEmail}
                    onChange={(e) => setFormData({ ...formData, userEmail: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg outline-none"
                    style={{
                      backgroundColor: theme?.backgroundTertiary || '#f5f5f5',
                      color: theme?.textPrimary || '#333',
                      borderColor: theme?.borderColor || '#e0e0e0'
                    }}
                    required
                  />
                </div>
                <div>
                  <label 
                    className="block mb-2 font-medium"
                    style={{ color: theme?.textPrimary || '#333' }}
                  >
                    Group Name *
                  </label>
                  <input
                    type="text"
                    value={formData.groupName}
                    onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg outline-none"
                    style={{
                      backgroundColor: theme?.backgroundTertiary || '#f5f5f5',
                      color: theme?.textPrimary || '#333',
                      borderColor: theme?.borderColor || '#e0e0e0'
                    }}
                    required
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    type="button"
                    onClick={handleCancel}
                    variant="outline"
                    style={{
                      backgroundColor: theme?.backgroundTertiary || '#f5f5f5',
                      color: theme?.textPrimary || '#333',
                      borderColor: theme?.borderColor || '#e0e0e0'
                    }}
                  >
                    {formMode === 'edit' && selectedUser ? 'Close' : 'Cancel'}
                  </Button>
                  {formMode === 'add' && (
                    <Button
                      type="submit"
                      style={{ backgroundColor: theme?.primary || '#7c4dff' }}
                    >
                      Create
                    </Button>
                  )}
                  {formMode === 'edit' && (
                    <Button
                      type="submit"
                      style={{ backgroundColor: theme?.primary || '#7c4dff' }}
                    >
                      Update
                    </Button>
                  )}
                </div>
              </form>
            </div>
          )}

          {!showForm && (
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="text-center py-8" style={{ color: theme?.textSecondary || '#666' }}>
                  Loading users...
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-8" style={{ color: theme?.textSecondary || '#666' }}>
                  {searchTerm ? 'No users found matching your search.' : 'No users found. Click "+" to add a user. Click "-" to delete users.'}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredUsers.map((user) => (
                    <div
                      key={user.id}
                      className={`p-4 border rounded-lg flex items-center justify-between hover:bg-opacity-50 transition-colors ${
                        deleteMode && usersToDelete.has(user.id) ? 'ring-2 ring-red-500' : ''
                      }`}
                      style={{
                        backgroundColor: deleteMode && usersToDelete.has(user.id)
                          ? 'rgba(239, 68, 68, 0.1)'
                          : theme?.backgroundSecondary || '#f5f7fb',
                        borderColor: theme?.borderColor || '#e0e0e0',
                        cursor: deleteMode ? 'pointer' : 'default'
                      }}
                      onClick={() => {
                        if (deleteMode) {
                          handleToggleUserForDelete(user.id);
                        }
                      }}
                    >
                      {deleteMode && (
                        <div className="mr-3">
                          <input
                            type="checkbox"
                            checked={usersToDelete.has(user.id)}
                            onChange={() => handleToggleUserForDelete(user.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-5 h-5"
                            style={{ accentColor: theme?.primary || '#7c4dff' }}
                          />
                        </div>
                      )}
                      <div className="flex-1">
                        <div 
                          className="font-medium"
                          style={{ color: theme?.textPrimary || '#333' }}
                        >
                          {user.userName || 'Unnamed User'}
                        </div>
                        <div 
                          className="text-sm mt-1"
                          style={{ color: theme?.textSecondary || '#666' }}
                        >
                          {user.userEmail}
                        </div>
                        <div 
                          className="text-sm mt-1"
                          style={{ color: theme?.textSecondary || '#666' }}
                        >
                          Group: {user.groupName || 'N/A'}
                        </div>
                      </div>
                      {!deleteMode && (
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleEditClick(user)}
                            variant="ghost"
                            size="icon"
                            style={{ 
                              color: theme?.primary || '#7c4dff',
                              backgroundColor: 'transparent'
                            }}
                            aria-label="Edit user"
                            title="Edit"
                          >
                            <Pencil size={18} />
                          </Button>
                          <Button
                            onClick={() => handleDeleteClick(user)}
                            variant="ghost"
                            size="icon"
                            style={{ 
                              color: '#ef4444',
                              backgroundColor: 'transparent'
                            }}
                            aria-label="Delete user"
                            title="Delete"
                          >
                            <Minus size={18} />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserCrudModal;
